import { useState } from "react";
import { AnalysisAPI, type DescriptiveEntry } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import VariablePicker from "../components/VariablePicker";
import PlotlyChart from "../components/Plot";
import { Card, PageHeader, Spinner, ErrorBanner } from "../components/Layout";
import type { Data } from "plotly.js-dist-min";

export default function DescriptiveReportPage() {
  const [projectId, setProjectId] = useState<number | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [report, setReport] = useState<Record<string, DescriptiveEntry> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (name: string) =>
    setSelected((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));

  const run = async () => {
    if (!projectId || !formId || selected.length === 0) return;
    setBusy(true); setErr(null);
    try {
      const r = await AnalysisAPI.descriptiveReport({
        project_id: projectId, form_id: formId, variables: selected,
      });
      setReport(r);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusy(false); }
  };

  const entries = report ? Object.entries(report).filter(([k]) => !k.startsWith("_")) : [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Informe descriptivo"
        subtitle="Selecciona variables y obtén un informe con frecuencias y margen de error."
      />
      <Card className="mb-4">
        <ProjectFormPicker
          projectId={projectId} formId={formId}
          onProject={(id) => { setProjectId(id); setFormId(null); setSelected([]); setReport(null); }}
          onForm={(id) => { setFormId(id); setSelected([]); setReport(null); }}
        />
        {formId && (
          <div className="mt-3">
            <label className="etiqueta">Variables</label>
            <VariablePicker
              projectId={projectId} formId={formId}
              selected={selected} onToggle={toggle}
              onSetSelected={setSelected}
              placeholder="Buscar pregunta…"
            />
          </div>
        )}
        <button
          onClick={run}
          disabled={busy || selected.length === 0}
          className="btn mt-3"
        >
          {busy ? <Spinner /> : "Generar informe"}
        </button>
      </Card>

      {err && <ErrorBanner message={err} />}

      {entries.length > 0 && (
        <div className="space-y-4">
          {entries.map(([name, info]) => (
            <Card key={name}>
              <div className="mb-2">
                <div className="text-sm text-tinta-2">{info.label || name}</div>
                <div className="text-[11px] text-tinta-3 font-mono">
                  {name} · {info.type} · n válido {info.n_valid}/{info.n_total}
                  {info.n_missing ? ` · ${info.n_missing} sin responder` : ""}
                </div>
              </div>

              {info.error ? (
                <div className="text-estado-critico text-sm">{info.error}</div>
              ) : info.kind === "numeric" && info.stats ? (
                <NumericStats stats={info.stats} />
              ) : info.categories ? (
                <CategoricalView categories={info.categories} />
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NumericStats({ stats }: { stats: Record<string, number> }) {
  const order = ["mean", "std", "min", "q1", "median", "q3", "max", "iqr"];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
      {order.filter((k) => stats[k] != null).map((k) => (
        <div key={k} className="bg-plano rounded p-2">
          <div className="text-[10px] uppercase text-tinta-3">{k}</div>
          <div className="text-marca">{stats[k].toFixed(2)}</div>
        </div>
      ))}
    </div>
  );
}

function CategoricalView({ categories }: { categories: Record<string, { n: number; pct: number; moe: number }> }) {
  const entries = Object.entries(categories).sort((a, b) => b[1].n - a[1].n);
  const trace: Data = {
    type: "bar",
    orientation: "h",
    x: entries.map(([, v]) => v.pct),
    y: entries.map(([k]) => k),
    marker: { color: "#0ea5e9" },
    error_x: { type: "data", array: entries.map(([, v]) => v.moe), thickness: 1.2 },
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="h-72">
        <PlotlyChart data={[trace]} layout={{ margin: { t: 10, r: 20, l: 160, b: 40 }, xaxis: { title: "%" } }} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-tinta-3 border-b border-grid">
            <tr>
              <th className="py-2 pr-3 text-left">Categoría</th>
              <th className="py-2 pr-3 text-right">n</th>
              <th className="py-2 pr-3 text-right">%</th>
              <th className="py-2 pr-3 text-right">±MoE</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([cat, st]) => (
              <tr key={cat} className="border-b border-grid">
                <td className="py-2 pr-3">{cat}</td>
                <td className="py-2 pr-3 text-right">{st.n}</td>
                <td className="py-2 pr-3 text-right text-marca">{st.pct.toFixed(1)}%</td>
                <td className="py-2 pr-3 text-right text-tinta-3">±{st.moe.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
